import {
    Box,
    Container,
    Grid,
    Stack,
    AspectRatio,
    Sheet,
    Typography,
    IconButton,
    Chip,
    Dropdown,
    MenuButton, Menu, MenuItem, Alert, Button, Divider
} from "@mui/joy";
import "firebase/compat/auth"
import React, {useContext, useEffect, useState} from "react";
import {AuthContext} from "../utils/AuthProvider";
import {Navbar} from "./Navbar";
import {Invoices, Subscription, UserDataModel} from "../utils/Model";
import {Cancel, CheckCircle, Edit, Help, MoreVert, Refresh, Sell} from "@mui/icons-material";
import moment from "moment";
import {Link} from "react-router-dom";
import {APIurl, intervalToString, stringPrice} from "../utils/Utils";
import firebase from "firebase/compat/app";
import {useTranslation} from "react-i18next";


export function Account() {

    const { i18n, t } = useTranslation();
    const {authState, userCompany, userData, subscriptions} = useContext(AuthContext)
    const defaultPrice = "€ 14,99"
    const [invoices, setInvoices] = useState<Invoices | null>(null)

    useEffect(() => {
        if(subscriptions.length > 0) {
            fetchInvoices().then(r => setInvoices(r.invoices))
        }
    }, [subscriptions])

    async function fetchInvoices(): Promise<{ invoices: Invoices }> {
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                throw new Error(`User not authenticated`);
            }

            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/getInvoices/${userData.uid}/${subscriptions[0].id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            return await response.json();

        } catch (error) {
            throw error;
        }
    }

    return (
        <Box sx={{height: '100vh'}}>
            <Navbar />
            <Container sx={{py: {xs:"7rem", md:"10rem"}}}>
                <Stack spacing={3}>

                    <Stack>
                        <Typography level="h2">{t("ciao")} {userData.name}!</Typography>
                        <Typography level="body-md">{t("manageabbonamento")}</Typography>
                    </Stack>
                    <Stack spacing={1}>
                        <Typography level="h4">{t("yourprofile")}</Typography>
                        <CardProfilo />
                    </Stack>
                    <Stack spacing={1}>
                        <Typography level="h4">{t("yoursubscription")}</Typography>
                        {
                            subscriptions.length !== 0 ?
                                subscriptions.map((subscription: Subscription) => <CardSubscription key={subscriptions.id} subscription={subscription} />)
                                :
                                <Alert variant="outlined" color="neutral">
                                    <Stack direction={{xs:"column", md:"row"}} spacing={2} justifyContent="space-between" alignItems="center" sx={{width:"100%"}}>
                                        <Stack textAlign={{xs:"center", md:"start"}}>
                                            <Typography level="title-md">{t("youdonthavesubscription")}</Typography>
                                            <Typography level="body-sm">{t("subscribelabel")}</Typography>
                                        </Stack>
                                        <Link to="/billing-details">
                                            <Button variant="solid" color="primary" startDecorator={<Sell />}>
                                                {t("subscribe")}
                                            </Button>
                                        </Link>
                                    </Stack>
                                </Alert>
                        }
                    </Stack>

                </Stack>
            </Container>
        </Box>
    )

    function CardProfilo() {
        return (
            <Sheet variant="outlined" sx={{width: '100%', p:3, borderRadius: 10}}>
                <Grid container spacing={2}>
                    <Grid xs={12} sm={6} md={2}>
                        <Typography level="title-sm">{t("name")}</Typography>
                        <Typography level="body-lg" fontWeight="bold">{userData.name}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={2}>
                        <Typography level="title-sm">{t("surname")}</Typography>
                        <Typography level="body-lg" fontWeight="bold">{userData.surname}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <Typography level="title-sm">{t("email")}</Typography>
                        <Typography level="body-lg" fontWeight="bold" fontSize={{xs:"0.85rem", md:"inherit"}}>{userData.email}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                        <Typography level="title-sm">{t("uid")}</Typography>
                        <Typography level="body-lg" fontFamily="monospace" fontSize={{xs:"0.85rem", md:"inherit"}}>{userData.uid}</Typography>
                    </Grid>
                </Grid>
            </Sheet>
        )
    }

    function CardSubscription(props: {subscription: Subscription}) {
        const subscription = props.subscription;

        switch (subscription.status) {
            case "active":
                return (
                    <Sheet variant="outlined" sx={{width: '100%', p:3, borderRadius: 10}}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm">{t("status")}</Typography>
                                <Chip size="md" variant="solid" color="success" startDecorator={<CheckCircle />}>{t("active")}</Chip>
                            </Grid>
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm">{t("startsubscription")}</Typography>
                                <Typography level="body-lg" fontWeight="bold">{moment(subscription.start_date, "X").format("DD/MM/YYYY")}</Typography>
                            </Grid>
                            {
                                subscription.cancel_at === null ?
                                    <Grid xs={6} md={2}>
                                        <Typography level="title-sm">{t("nextrenew")}</Typography>
                                        <Typography level="body-lg" fontWeight="bold">{moment(subscription.current_period_end, "X").format("DD/MM/YYYY")}</Typography>
                                    </Grid>
                                    :
                                    null
                            }
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm">{t("frequency")}</Typography>
                                <Typography level="body-lg" fontWeight="bold">{intervalToString(subscription.items.data[0].plan.interval)}</Typography>
                            </Grid>
                            <Grid xs={6} md={2}>
                                <Typography level="title-sm">{t("annualprice")}</Typography>
                                <Typography level="body-lg" fontWeight="bold">{defaultPrice}</Typography>
                            </Grid>
                            <Grid xs={12}>
                                <Divider />
                                <Typography level="title-sm" mt={3} fontWeight="bold">{t("invoices")}</Typography>
                            </Grid>
                            <Grid xs={12}>
                                <Stack direction="column" spacing={3}>
                                {
                                    invoices?.data.sort((a, b) => b.created - a.created).map(inv =>
                                        <Grid container spacing={2} alignItems="center" key={inv.id}>
                                            <Grid xs={6} md={2}>
                                                <Stack direction="column" spacing={0}>
                                                    <Typography level="title-sm">{t("invoicenumber")}</Typography>
                                                    <Typography level="body-lg" fontWeight="bold">{inv.number}</Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid xs={6} md={2}>
                                                <Stack direction="column" spacing={0}>
                                                    <Typography level="title-sm">{t("date")}</Typography>
                                                    <Typography level="body-lg" fontWeight="bold">{moment(inv.created, "X").format("DD/MM/YYYY")}</Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid xs={6} md={2}>
                                                <Stack direction="column" spacing={0}>
                                                    <Typography level="title-sm">{t("price")}</Typography>
                                                    <Typography level="body-lg" fontWeight="bold">{stringPrice(inv.currency, inv.amount_paid)}</Typography>
                                                </Stack>
                                            </Grid>
                                            <Grid xs={6} md={2}>
                                                <Stack direction="column" spacing={0}>
                                                    <a href={inv.hosted_invoice_url!} rel="noreferrer" target="_blank">
                                                        <Button color="primary" variant="solid">{t("downloadinvoice")}</Button>
                                                    </a>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    )
                                }
                                </Stack>
                            </Grid>
                            {
                                subscription.cancel_at !== null ?
                                    <Grid xs={12}>
                                        <Alert variant="soft" color="warning">
                                            <Stack>
                                                <Typography level="body-sm">{t("indata")} {moment(subscription.canceled_at, "X").format("DD/MM/YYYY")} {t("hairichiestoannullamento")}</Typography>
                                                <Typography level="body-sm">{t("subscriptionwillbeinterruptedon")} <b>{moment(subscription.cancel_at, "X").format("DD/MM/YYYY")}</b>.</Typography>
                                            </Stack>
                                        </Alert>
                                    </Grid>
                                    :
                                    null
                            }
                        </Grid>
                        <Dropdown>
                            <MenuButton
                                slots={{ root: IconButton }}
                                slotProps={{ root: { variant: 'outlined', color: 'neutral' } }}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    mr: "-1rem",
                                    mt: "-1rem",
                                    backgroundColor: "var(--sidebar_-palette-background-surface)",
                                    borderRadius: 100
                                }}
                            >
                                <MoreVert />
                            </MenuButton>
                            <Menu>
                                {
                                    subscription.cancel_at !== null ?
                                    <MenuItem color="primary"
                                              onClick={() => {
                                                  if(window.confirm(t("restoresubscriptionquestion"))) {
                                                      restoreSubscription(subscription.id)
                                                          .then(() => {
                                                              window.location.reload();
                                                          })
                                                          .catch(e => alert(e.message))
                                                  }
                                              }}
                                    >
                                        <Typography level="title-sm" color="primary" startDecorator={<Refresh />}>{t("restoresubscription")}</Typography>
                                    </MenuItem>
                                        :
                                    <MenuItem color="danger"
                                              onClick={() => {
                                                  if(window.confirm(t("deletesubscriptionquestion"))) {
                                                      deleteSubscription(subscription.id)
                                                          .then(() => {
                                                              window.location.reload();
                                                          })
                                                          .catch(e => alert(e.message))
                                                  }
                                              }}
                                    >
                                        <Typography level="title-sm" color="danger" startDecorator={<Cancel />}>{t("deletesubscription")}</Typography>
                                    </MenuItem>
                                }
                            </Menu>
                        </Dropdown>
                    </Sheet>
                )
            case "canceled":
                return <Chip size="md" variant="solid" color="danger" startDecorator={<Cancel />}>{t("cancelled")}</Chip>
            case "ended":
                return <Chip size="md" variant="solid" color="danger" startDecorator={<Cancel />}>{t("ended")}</Chip>
            default:
                return <Chip size="md" variant="solid" color="neutral" startDecorator={<Help />}>{t("notavailable")}</Chip>
        }
    }

    async function deleteSubscription(id: string) {
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                return
            }
            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/deleteSubscription/${id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
            if (!response.ok) {
                return;
            }

            return await response.json();
        } catch (e: unknown) {
            alert(e)
        }
    }

    async function restoreSubscription(id: string) {
        try {
            const user = firebase.auth().currentUser;
            if (!user) {
                return
            }
            const token = await user.getIdToken();

            const response = await fetch(`${APIurl}/restoreSubscription/${id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            })
            if (!response.ok) {
                return;
            }

            return await response.json();
        } catch (e: unknown) {
            alert(e)
        }
    }
}